/**
 * MCPView Component
 *
 * MCP (Model Context Protocol) server management for projects:
 * - View auto-generated MCP configuration
 * - Generate MCP servers for projects
 * - Download Claude Desktop configuration
 * - Test MCP servers via Inspector
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Plug, Server, Download, ExternalLink, RefreshCw,
  CheckCircle, AlertCircle, Loader2, Copy, Code,
  FileJson, Terminal, Play, Settings, Folder, Database, Trash2,
  MessageSquare, Send, Wrench, Clock, X, ChevronUp, ChevronDown,
  Plus, Globe, Link, Maximize2, Minimize2
} from 'lucide-react'
import {
  generateMCP,
  getMCPStatus,
  getClaudeConfig,
  getClaudeConfigDownloadUrl,
  getMCPInspectorUrl,
  getProjectTools,
  deleteMCP,
  sendMCPChat,
  startMCPServer,
  stopMCPServer,
  getMCPServerStatus,
} from '../../services/mcpService'
import './MCPView.css'

/**
 * Tool card component
 */
function ToolCard({ tool }) {
  return (
    <div className="mcp-tool-card">
      <div className="tool-name">
        <Code size={14} />
        <span>{tool.name}</span>
      </div>
      <div className="tool-description">{tool.description}</div>
      {tool.parameters && tool.parameters.length > 0 && (
        <div className="tool-params">
          {tool.parameters.map((param, i) => (
            <span key={i} className="param-badge">{param}</span>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Status badge component
 */
function StatusBadge({ exists, label }) {
  return (
    <span className={`status-badge ${exists ? 'success' : 'warning'}`}>
      {exists ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
      {label}
    </span>
  )
}

/**
 * Chat message component
 */
function ChatMessage({ message }) {
  const isUser = message.role === 'user'

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-header">
        <span className="message-role">{isUser ? 'You' : 'AI Assistant'}</span>
        {message.tools_used && message.tools_used.length > 0 && (
          <span className="tools-used">
            <Wrench size={12} />
            {message.tools_used.join(', ')}
          </span>
        )}
        {message.duration_ms && (
          <span className="message-duration">
            <Clock size={12} />
            {(message.duration_ms / 1000).toFixed(1)}s
          </span>
        )}
      </div>
      <div className="message-content">
        {message.content}
      </div>
    </div>
  )
}

/**
 * Main MCPView component
 */
function MCPView({ project }) {
  const [status, setStatus] = useState(null)
  const [config, setConfig] = useState(null)
  const [tools, setTools] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [inspectorUrl, setInspectorUrl] = useState(null)

  // Chat state
  const [chatOpen, setChatOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [chatError, setChatError] = useState(null)
  const chatEndRef = useRef(null)
  const chatPanelRef = useRef(null)

  // Chat panel detachable state
  const [chatDetached, setChatDetached] = useState(false)
  const [chatPosition, setChatPosition] = useState({ x: 100, y: 100 })
  const [chatDragging, setChatDragging] = useState(false)
  const [chatDragOffset, setChatDragOffset] = useState({ x: 0, y: 0 })

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Add Tool modal state
  const [addToolOpen, setAddToolOpen] = useState(false)
  const [toolType, setToolType] = useState('url') // 'url', 'api', or 'database'
  const [customTools, setCustomTools] = useState([]) // Pending tools in modal
  const [savedCustomTools, setSavedCustomTools] = useState([]) // Saved custom tools
  const [newTool, setNewTool] = useState({
    name: '',
    description: '',
    // API fields
    url: '',
    method: 'GET',
    body: '', // For POST requests
    // Database fields
    dbType: 'sqlite',
    connectionString: '',
    query: ''
  })

  // Activity log state
  const [logs, setLogs] = useState([])
  const [logCollapsed, setLogCollapsed] = useState(false)
  const logContainerRef = useRef(null)

  // Helper to add log entry
  const addLog = useCallback((message, level = 'info', node = null) => {
    setLogs(prev => [...prev, {
      timestamp: new Date(),
      message,
      level,
      node
    }])
  }, [])

  // Clear logs
  const clearLogs = useCallback(() => {
    setLogs([])
  }, [])

  // Chat panel drag handlers
  const handleChatMouseDown = useCallback((e) => {
    if (!chatDetached) return
    if (e.target.closest('button')) return

    setChatDragging(true)
    const rect = chatPanelRef.current?.getBoundingClientRect()
    if (rect) {
      setChatDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
    }
    e.preventDefault()
  }, [chatDetached])

  const handleChatMouseMove = useCallback((e) => {
    if (!chatDragging) return
    const newX = Math.max(0, Math.min(window.innerWidth - 500, e.clientX - chatDragOffset.x))
    const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - chatDragOffset.y))
    setChatPosition({ x: newX, y: newY })
  }, [chatDragging, chatDragOffset])

  const handleChatMouseUp = useCallback(() => {
    setChatDragging(false)
  }, [])

  // Add global mouse listeners for chat dragging
  useEffect(() => {
    if (chatDragging) {
      window.addEventListener('mousemove', handleChatMouseMove)
      window.addEventListener('mouseup', handleChatMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleChatMouseMove)
        window.removeEventListener('mouseup', handleChatMouseUp)
      }
    }
  }, [chatDragging, handleChatMouseMove, handleChatMouseUp])

  // Toggle chat detached mode
  const toggleChatDetached = () => {
    if (!chatDetached) {
      setChatPosition({
        x: Math.max(50, (window.innerWidth - 500) / 2),
        y: Math.max(50, (window.innerHeight - 600) / 2)
      })
    }
    setChatDetached(!chatDetached)
  }

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current && !logCollapsed) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [logs, logCollapsed])

  // Extract project details - project has 'account' and 'name' fields
  const accountId = project?.scout_account_id || project?.accountId || project?.account
  const appName = project?.application_name || project?.applicationName || project?.name

  // Fetch MCP data
  const fetchData = useCallback(async () => {
    if (!accountId || !appName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch status, config, tools, and inspector URL in parallel
      const [statusData, configData, toolsData, inspectorData] = await Promise.all([
        getMCPStatus(accountId, appName).catch(() => null),
        getClaudeConfig(accountId, appName).catch(() => null),
        getProjectTools(accountId, appName).catch(() => ({ tools: [] })),
        getMCPInspectorUrl().catch(() => ({ url: 'http://localhost:6274' })),
      ])

      setStatus(statusData)
      setConfig(configData)
      setTools(toolsData?.tools || [])
      setInspectorUrl(inspectorData?.url || 'http://localhost:6274')
    } catch (err) {
      console.error('Failed to fetch MCP data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [accountId, appName])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Load saved custom tools from localStorage
  useEffect(() => {
    if (accountId && appName) {
      const storageKey = `mcp_custom_tools_${accountId}_${appName}`
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        try {
          setSavedCustomTools(JSON.parse(saved))
        } catch (e) {
          console.error('Failed to parse saved custom tools:', e)
        }
      }
    }
  }, [accountId, appName])

  // Generate MCP server
  const handleGenerate = async () => {
    if (!accountId || !appName) return

    setGenerating(true)
    setError(null)
    addLog(`Generating MCP server for ${accountId}/${appName}...`, 'start', 'Generate')

    try {
      // Include saved custom tools when regenerating
      const result = await generateMCP(accountId, appName, savedCustomTools.length > 0 ? savedCustomTools : null)
      addLog(`MCP server generated successfully`, 'success', 'Generate')
      addLog(`Server path: ${result.server_path}`, 'info', 'Generate')
      addLog(`Files created: ${result.files?.join(', ') || 'server.py, Dockerfile, etc.'}`, 'info', 'Generate')

      // Show Docker container status
      if (result.container_running) {
        addLog(`Docker container started: ${result.container_name}`, 'success', 'Docker')
        addLog(`Server running at: ${result.container_url}`, 'info', 'Docker')
      } else {
        addLog(`Docker container: ${result.container_name} (not running)`, 'warning', 'Docker')
        if (result.docker_error) {
          addLog(`Docker error: ${result.docker_error}`, 'error', 'Docker')
        }
      }

      // Refresh data after generation
      await fetchData()
    } catch (err) {
      console.error('Failed to generate MCP:', err)
      addLog(`Failed to generate MCP: ${err.message}`, 'error', 'Generate')
      setError(err.message)
    } finally {
      setGenerating(false)
    }
  }

  // Delete MCP server
  const handleDelete = async () => {
    if (!accountId || !appName) return
    if (!confirm('Delete MCP server? You can regenerate it anytime.')) return

    setDeleting(true)
    setError(null)
    addLog(`Deleting MCP server...`, 'warning', 'Delete')

    try {
      await deleteMCP(accountId, appName)
      addLog(`MCP server deleted successfully`, 'success', 'Delete')
      // Refresh data after deletion
      await fetchData()
    } catch (err) {
      console.error('Failed to delete MCP:', err)
      addLog(`Failed to delete MCP: ${err.message}`, 'error', 'Delete')
      setError(err.message)
    } finally {
      setDeleting(false)
    }
  }

  // Copy config to clipboard
  const handleCopyConfig = async () => {
    if (!config) return

    try {
      await navigator.clipboard.writeText(JSON.stringify(config, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Download config file
  const handleDownload = () => {
    if (!accountId || !appName) return
    window.open(getClaudeConfigDownloadUrl(accountId, appName), '_blank')
  }

  // Open MCP Inspector
  const handleOpenInspector = () => {
    window.open(inspectorUrl, '_blank')
  }

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  // Send chat message
  const handleSendChat = async () => {
    const message = chatInput.trim()
    if (!message || chatLoading || !accountId || !appName) return

    // Add user message
    const userMessage = { role: 'user', content: message }
    setChatMessages(prev => [...prev, userMessage])
    setChatInput('')
    setChatLoading(true)
    setChatError(null)

    try {
      // Check if Docker container is running, start if needed
      addLog(`Checking MCP server container...`, 'start', 'Docker')
      const serverStatus = await getMCPServerStatus(accountId, appName)

      if (!serverStatus.running) {
        addLog(`Starting MCP server in Docker (this may take a moment)...`, 'info', 'Docker')
        const startResult = await startMCPServer(accountId, appName)
        addLog(`Container started: ${startResult.container_name} on port ${startResult.port}`, 'success', 'Docker')
      } else {
        addLog(`Container already running: ${serverStatus.container_name}`, 'info', 'Docker')
      }

      addLog(`Sending chat message...`, 'start', 'Chat')

      // Build history (exclude current message)
      const history = chatMessages.map(m => ({
        role: m.role,
        content: m.content
      }))

      const response = await sendMCPChat(accountId, appName, message, history)

      // Add assistant message
      const assistantMessage = {
        role: 'assistant',
        content: response.response,
        tools_used: response.tools_used,
        duration_ms: response.duration_ms
      }
      setChatMessages(prev => [...prev, assistantMessage])

      if (response.tools_used?.length > 0) {
        addLog(`Tools used: ${response.tools_used.join(', ')}`, 'info', 'Chat')
      }
      addLog(`Response received (${(response.duration_ms / 1000).toFixed(1)}s)`, 'success', 'Chat')
    } catch (err) {
      console.error('Chat error:', err)
      addLog(`Chat error: ${err.message}`, 'error', 'Chat')
      setChatError(err.message)
    } finally {
      setChatLoading(false)
    }
  }

  // Handle enter key in chat input
  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendChat()
    }
  }

  // Clear chat history
  const handleClearChat = () => {
    setChatMessages([])
    setChatError(null)
  }

  // No project selected
  if (!project) {
    return (
      <div className="mcp-view">
        <div className="mcp-empty">
          <Plug size={48} />
          <h2>No Project Selected</h2>
          <p>Select a project from the landing page to manage its MCP server.</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="mcp-view">
        <div className="mcp-loading">
          <Loader2 size={32} className="spinning" />
          <p>Loading MCP configuration...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mcp-view">
      <div className="mcp-container">
        {/* Header */}
        <div className="mcp-header">
          <div className="header-title">
            <Plug size={24} />
            <div>
              <h1>MCP Server</h1>
              <p className="header-subtitle">{accountId} / {appName}</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="mcp-btn primary chat-btn"
              onClick={() => setChatOpen(true)}
              disabled={!status?.server_exists}
              title={!status?.server_exists ? 'Generate MCP first' : 'Ask AI about your project'}
            >
              <MessageSquare size={16} />
              Ask AI
            </button>
            <button
              className="mcp-btn icon"
              onClick={() => setSettingsOpen(true)}
              title="MCP Settings"
            >
              <Settings size={18} />
            </button>
            <button
              className="mcp-btn secondary"
              onClick={fetchData}
              disabled={loading || generating}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
              Refresh
            </button>
            {status?.server_exists && (
              <button
                className="mcp-btn danger"
                onClick={handleDelete}
                disabled={deleting || generating}
              >
                {deleting ? (
                  <Loader2 size={16} className="spinning" />
                ) : (
                  <Trash2 size={16} />
                )}
                Delete
              </button>
            )}
            <button
              className="mcp-btn primary"
              onClick={handleGenerate}
              disabled={generating || deleting}
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Generating...
                </>
              ) : status?.server_exists ? (
                <>
                  <RefreshCw size={16} />
                  Regenerate
                </>
              ) : (
                <>
                  <Play size={16} />
                  Generate MCP
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mcp-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Status Cards */}
        <div className="mcp-status-grid">
          <div className="status-card">
            <div className="status-icon server">
              <Server size={20} />
            </div>
            <div className="status-content">
              <h3>MCP Server</h3>
              <StatusBadge
                exists={status?.server_exists}
                label={status?.server_exists ? 'Generated' : 'Not Generated'}
              />
            </div>
          </div>

          <div className="status-card">
            <div className="status-icon folder">
              <Folder size={20} />
            </div>
            <div className="status-content">
              <h3>Source Files</h3>
              <StatusBadge
                exists={status?.source_exists}
                label={status?.source_exists ? 'Available' : 'Not Found'}
              />
            </div>
          </div>

          <div className="status-card">
            <div className="status-icon database">
              <Database size={20} />
            </div>
            <div className="status-content">
              <h3>Artifacts</h3>
              <StatusBadge
                exists={status?.artifacts_exist}
                label={status?.artifacts_exist ? 'Available' : 'Not Found'}
              />
            </div>
          </div>

          <div className="status-card">
            <div className="status-icon config">
              <FileJson size={20} />
            </div>
            <div className="status-content">
              <h3>Config</h3>
              <StatusBadge
                exists={status?.config_exists}
                label={status?.config_exists ? 'Ready' : 'Not Created'}
              />
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="mcp-content-grid">
          {/* Left: Claude Config */}
          <div className="mcp-section">
            <div className="section-header">
              <h2>
                <FileJson size={18} />
                Claude Desktop Configuration
              </h2>
              <div className="section-actions">
                <button
                  className="mcp-btn icon"
                  onClick={handleCopyConfig}
                  disabled={!config}
                  title="Copy to clipboard"
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
                <button
                  className="mcp-btn icon"
                  onClick={handleDownload}
                  disabled={!status?.server_exists}
                  title="Download config file"
                >
                  <Download size={16} />
                </button>
              </div>
            </div>

            <div className="config-preview">
              {config ? (
                <pre>{JSON.stringify(config, null, 2)}</pre>
              ) : (
                <div className="config-empty">
                  <Settings size={24} />
                  <p>Generate MCP server to see configuration</p>
                </div>
              )}
            </div>

            <div className="config-instructions">
              <h3>Setup Instructions</h3>
              <ol>
                <li>Click <strong>Generate MCP</strong> above to create the server</li>
                <li>Copy the configuration or download the JSON file</li>
                <li>Add to <code>~/Library/Application Support/Claude/claude_desktop_config.json</code></li>
                <li>Restart Claude Desktop</li>
              </ol>
            </div>
          </div>

          {/* Right: Tools & Testing */}
          <div className="mcp-section">
            <div className="section-header">
              <h2>
                <Code size={18} />
                Available Tools
              </h2>
              <div className="section-actions">
                <span className="tool-count">{tools.length + savedCustomTools.length} tools</span>
                <button
                  className="mcp-btn secondary small"
                  onClick={() => setAddToolOpen(true)}
                  title="Add custom tool"
                >
                  <Plus size={14} />
                  Add Tool
                </button>
              </div>
            </div>

            <div className="tools-list">
              {/* Built-in tools */}
              {tools.length > 0 ? (
                tools.map((tool, i) => <ToolCard key={i} tool={tool} />)
              ) : (
                <div className="tools-empty">
                  <Code size={24} />
                  <p>Tools will appear after generating MCP server</p>
                </div>
              )}
              {/* Custom tools */}
              {savedCustomTools.length > 0 && (
                <>
                  <div className="custom-tools-divider">
                    <span>Custom Tools</span>
                  </div>
                  {savedCustomTools.map((tool, i) => (
                    <div key={`custom-${i}`} className="mcp-tool-card custom">
                      <div className="tool-name">
                        {tool.type === 'url' ? <Link size={14} /> : tool.type === 'api' ? <Globe size={14} /> : <Database size={14} />}
                        <span>{tool.name}</span>
                        <span className="custom-badge">{tool.type === 'url' ? 'URL' : tool.type === 'api' ? 'API' : 'DB'}</span>
                        <button
                          className="custom-tool-delete"
                          onClick={async () => {
                            const updatedTools = savedCustomTools.filter((_, idx) => idx !== i)
                            const storageKey = `mcp_custom_tools_${accountId}_${appName}`
                            localStorage.setItem(storageKey, JSON.stringify(updatedTools))
                            setSavedCustomTools(updatedTools)
                            // Regenerate MCP with updated tools
                            addLog(`Removing custom tool: ${tool.name}...`, 'warning', 'Tools')
                            try {
                              await generateMCP(accountId, appName, updatedTools)
                              addLog(`Regenerated MCP without ${tool.name}`, 'success', 'Tools')
                            } catch (err) {
                              addLog(`Failed to regenerate: ${err.message}`, 'error', 'Tools')
                            }
                          }}
                          title="Remove this tool"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <div className="tool-description">{tool.description}</div>
                      <div className="tool-url">
                        {tool.type === 'database' ? tool.connectionString : tool.url}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Testing Section */}
            <div className="testing-section">
              <h3>
                <Terminal size={16} />
                Test Your MCP Server
              </h3>
              <p>Use MCP Inspector to test tools before connecting to Claude:</p>

              <button
                className="mcp-btn secondary full-width"
                onClick={handleOpenInspector}
              >
                <ExternalLink size={16} />
                Open MCP Inspector
              </button>

              <div className="terminal-command">
                <code>npx @anthropic-ai/mcp-inspector uv run server.py</code>
                <button
                  className="copy-btn"
                  onClick={() => navigator.clipboard.writeText('npx @anthropic-ai/mcp-inspector uv run server.py')}
                  title="Copy command"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            {/* Path Info */}
            {status?.mcp_dir && (
              <div className="path-info">
                <h3>
                  <Folder size={16} />
                  Server Location
                </h3>
                <code className="path-code">{status.mcp_dir}</code>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Activity Log Panel */}
      <div className={`mcp-log-panel ${logCollapsed ? 'collapsed' : ''}`}>
        <div className="log-panel-header" onClick={() => setLogCollapsed(!logCollapsed)}>
          <div className="log-panel-title">
            <Terminal size={14} />
            <span>Activity Log</span>
            {logs.length > 0 && (
              <span className="log-count">{logs.length}</span>
            )}
          </div>
          <div className="log-panel-actions">
            {!logCollapsed && logs.length > 0 && (
              <button
                className="log-action-btn"
                onClick={(e) => { e.stopPropagation(); clearLogs() }}
                title="Clear logs"
              >
                <Trash2 size={14} />
              </button>
            )}
            <button className="log-toggle-btn" title={logCollapsed ? 'Expand' : 'Collapse'}>
              {logCollapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {!logCollapsed && (
          <div className="log-panel-content" ref={logContainerRef}>
            {logs.length === 0 ? (
              <div className="log-empty">
                <span>No activity yet. Generate an MCP server or use Ask AI to see logs.</span>
              </div>
            ) : (
              logs.map((log, index) => (
                <div key={index} className={`log-entry log-${log.level || 'info'}`}>
                  <span className="log-timestamp">
                    [{log.timestamp.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]
                  </span>
                  {log.node && <span className="log-node">[{log.node}]</span>}
                  <span className="log-message">{log.message}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {settingsOpen && (
        <div className="chat-modal-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <div className="chat-modal-title">
                <Settings size={20} />
                <span>MCP Settings</span>
              </div>
              <button
                className="chat-modal-close"
                onClick={() => setSettingsOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="settings-content">
              <div className="settings-section">
                <h3>Project Paths</h3>
                <div className="settings-item">
                  <label>Source Files</label>
                  <code>{status?.source_path || 'Not configured'}</code>
                  <span className={`settings-status ${status?.source_exists ? 'success' : 'warning'}`}>
                    {status?.source_exists ? 'Available' : 'Not Found'}
                  </span>
                </div>
                <div className="settings-item">
                  <label>Artifacts</label>
                  <code>{status?.artifacts_path || 'Not configured'}</code>
                  <span className={`settings-status ${status?.artifacts_exist ? 'success' : 'warning'}`}>
                    {status?.artifacts_exist ? 'Available' : 'Not Found'}
                  </span>
                </div>
              </div>

              <div className="settings-section">
                <h3>Claude Desktop Server</h3>
                <div className="settings-item">
                  <label>Server Status</label>
                  <span className={`settings-status ${status?.server_exists ? 'success' : 'warning'}`}>
                    {status?.server_exists ? 'Generated' : 'Not Generated'}
                  </span>
                </div>
                {status?.mcp_dir && (
                  <div className="settings-item">
                    <label>Server Location</label>
                    <code>{status.mcp_dir}</code>
                  </div>
                )}
              </div>

              <div className="settings-section">
                <h3>Available Tools</h3>
                <div className="settings-tools">
                  {tools.length > 0 ? (
                    tools.map((tool, i) => (
                      <div key={i} className="settings-tool">
                        <span className="tool-name">{tool.name}</span>
                        <span className="tool-desc">{tool.description}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-tools">No tools available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal - Detachable */}
      {chatOpen && !chatDetached && (
        <div className="chat-modal-overlay" onClick={() => setChatOpen(false)} />
      )}
      {chatOpen && (
        <div
          ref={chatPanelRef}
          className={`chat-modal ${chatDetached ? 'detached' : ''} ${chatDragging ? 'dragging' : ''}`}
          style={chatDetached ? { left: chatPosition.x, top: chatPosition.y } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`chat-modal-header ${chatDetached ? 'draggable' : ''}`}
            onMouseDown={handleChatMouseDown}
          >
            <div className="chat-modal-title">
              <MessageSquare size={20} />
              <span>Ask AI About Your Project</span>
            </div>
            <div className="chat-modal-actions">
              {chatMessages.length > 0 && (
                <button
                  className="mcp-btn secondary small"
                  onClick={handleClearChat}
                >
                  Clear
                </button>
              )}
              <button
                className="chat-modal-btn"
                onClick={toggleChatDetached}
                title={chatDetached ? 'Dock panel' : 'Detach panel'}
              >
                {chatDetached ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                className="chat-modal-close"
                onClick={() => setChatOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="chat-messages">
              {chatMessages.length === 0 ? (
                <div className="chat-empty">
                  <MessageSquare size={32} />
                  <p>Ask questions about your COBOL files, Java code, or analysis results.</p>
                  <div className="chat-suggestions">
                    <button onClick={() => setChatInput('What COBOL files are in this project?')}>
                      What COBOL files are in this project?
                    </button>
                    <button onClick={() => setChatInput('Show me the main program structure')}>
                      Show me the main program structure
                    </button>
                    <button onClick={() => setChatInput('What analysis results are available?')}>
                      What analysis results are available?
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((msg, i) => (
                    <ChatMessage key={i} message={msg} />
                  ))}
                  {chatLoading && (
                    <div className="chat-message assistant loading">
                      <div className="message-header">
                        <span className="message-role">AI Assistant</span>
                      </div>
                      <div className="message-content">
                        <Loader2 size={16} className="spinning" />
                        <span>Analyzing your project...</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>

            {chatError && (
              <div className="chat-error">
                <AlertCircle size={14} />
                <span>{chatError}</span>
              </div>
            )}

            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder="Ask about your project..."
                disabled={chatLoading}
                autoFocus
              />
              <button
                className="chat-send-btn"
                onClick={handleSendChat}
                disabled={!chatInput.trim() || chatLoading}
              >
                {chatLoading ? (
                  <Loader2 size={18} className="spinning" />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </div>
          </div>
        )}

      {/* Add Tool Modal */}
      {addToolOpen && (
        <div className="chat-modal-overlay" onClick={() => setAddToolOpen(false)}>
          <div className="add-tool-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <div className="chat-modal-title">
                <Plus size={20} />
                <span>Add Custom Tool</span>
              </div>
              <button
                className="chat-modal-close"
                onClick={() => setAddToolOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="add-tool-content">
              {/* Tool Type Tabs */}
              <div className="tool-type-tabs">
                <button
                  className={`tool-type-tab ${toolType === 'url' ? 'active' : ''}`}
                  onClick={() => setToolType('url')}
                >
                  <Link size={16} />
                  URL / Docs
                </button>
                <button
                  className={`tool-type-tab ${toolType === 'api' ? 'active' : ''}`}
                  onClick={() => setToolType('api')}
                >
                  <Globe size={16} />
                  API
                </button>
                <button
                  className={`tool-type-tab ${toolType === 'database' ? 'active' : ''}`}
                  onClick={() => setToolType('database')}
                >
                  <Database size={16} />
                  Database
                </button>
              </div>

              {/* Form Fields */}
              <div className="add-tool-form">
                <div className="form-group">
                  <label>Tool Name</label>
                  <input
                    type="text"
                    placeholder="e.g., search_products"
                    value={newTool.name}
                    onChange={(e) => setNewTool({ ...newTool, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    placeholder="What does this tool do?"
                    value={newTool.description}
                    onChange={(e) => setNewTool({ ...newTool, description: e.target.value })}
                  />
                </div>

                {toolType === 'url' ? (
                  <div className="form-group">
                    <label>URL</label>
                    <input
                      type="text"
                      placeholder="https://www.ibm.com/docs/en/i/7.5.0?topic=programs-cobol-java"
                      value={newTool.url}
                      onChange={(e) => setNewTool({ ...newTool, url: e.target.value })}
                    />
                    <small style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      Documentation or reference page to fetch and read
                    </small>
                  </div>
                ) : toolType === 'api' ? (
                  <>
                    <div className="form-group">
                      <label>URL</label>
                      <input
                        type="text"
                        placeholder="https://api.example.com/search"
                        value={newTool.url}
                        onChange={(e) => setNewTool({ ...newTool, url: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Method</label>
                      <select
                        value={newTool.method}
                        onChange={(e) => setNewTool({ ...newTool, method: e.target.value })}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>
                    </div>
                    {newTool.method === 'POST' && (
                      <div className="form-group">
                        <label>Request Body (JSON)</label>
                        <textarea
                          placeholder='{"key": "value", "query": "${query}"}'
                          value={newTool.body}
                          onChange={(e) => setNewTool({ ...newTool, body: e.target.value })}
                          rows={4}
                        />
                        <small style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                          Use ${'{query}'} as placeholder for the search parameter
                        </small>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Database Type</label>
                      <select
                        value={newTool.dbType}
                        onChange={(e) => setNewTool({ ...newTool, dbType: e.target.value })}
                      >
                        <option value="sqlite">SQLite</option>
                        <option value="postgresql">PostgreSQL</option>
                        <option value="mysql">MySQL</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{newTool.dbType === 'sqlite' ? 'Database Path' : 'Connection String'}</label>
                      <input
                        type="text"
                        placeholder={newTool.dbType === 'sqlite' ? '/path/to/database.db' : 'postgresql://user:pass@host:5432/db'}
                        value={newTool.connectionString}
                        onChange={(e) => setNewTool({ ...newTool, connectionString: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Query Template</label>
                      <textarea
                        placeholder="SELECT * FROM users WHERE name LIKE ?"
                        value={newTool.query}
                        onChange={(e) => setNewTool({ ...newTool, query: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Custom Tools List */}
              {customTools.length > 0 && (
                <div className="custom-tools-list">
                  <h4>Custom Tools to Add</h4>
                  {customTools.map((tool, i) => (
                    <div key={i} className="custom-tool-item">
                      <span className="custom-tool-icon">
                        {tool.type === 'url' ? <Link size={14} /> : tool.type === 'api' ? <Globe size={14} /> : <Database size={14} />}
                      </span>
                      <span className="custom-tool-name">{tool.name}</span>
                      <button
                        className="custom-tool-remove"
                        onClick={() => setCustomTools(customTools.filter((_, idx) => idx !== i))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="add-tool-actions">
                <button
                  className="mcp-btn secondary"
                  onClick={() => {
                    if (newTool.name && (newTool.url || newTool.connectionString)) {
                      setCustomTools([...customTools, { ...newTool, type: toolType }])
                      setNewTool({
                        name: '',
                        description: '',
                        url: '',
                        method: 'GET',
                        body: '',
                        dbType: 'sqlite',
                        connectionString: '',
                        query: ''
                      })
                    }
                  }}
                  disabled={!newTool.name || (toolType === 'database' ? !newTool.connectionString : !newTool.url)}
                >
                  <Plus size={16} />
                  Add to List
                </button>
                <button
                  className="mcp-btn primary"
                  onClick={async () => {
                    // Save and regenerate
                    const allTools = [...customTools]
                    if (newTool.name && (newTool.url || newTool.connectionString)) {
                      allTools.push({ ...newTool, type: toolType })
                    }
                    if (allTools.length > 0) {
                      // Merge with existing saved tools
                      const mergedTools = [...savedCustomTools, ...allTools]
                      addLog(`Adding ${allTools.length} custom tool(s)...`, 'start', 'Tools')
                      try {
                        const result = await generateMCP(accountId, appName, mergedTools)
                        addLog(`Regenerated MCP with custom tools`, 'success', 'Tools')
                        // Save to localStorage
                        const storageKey = `mcp_custom_tools_${accountId}_${appName}`
                        localStorage.setItem(storageKey, JSON.stringify(mergedTools))
                        setSavedCustomTools(mergedTools)
                        setCustomTools([])
                        setNewTool({
                          name: '',
                          description: '',
                          url: '',
                          method: 'GET',
                          body: '',
                          dbType: 'sqlite',
                          connectionString: '',
                          query: ''
                        })
                        setAddToolOpen(false)
                        await fetchData()
                      } catch (err) {
                        addLog(`Failed to add tools: ${err.message}`, 'error', 'Tools')
                      }
                    }
                  }}
                  disabled={customTools.length === 0 && (!newTool.name || (toolType === 'database' ? !newTool.connectionString : !newTool.url))}
                >
                  Save & Regenerate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MCPView
